import { Component, effect, inject, signal } from '@angular/core';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { Session } from '../../../services/session';
import { SchoolService } from '../../../services/school';
import { ISchool } from '../../../interfaces/school';
import { FlashService } from '../../../services/flash';
import { Helpers } from '../../../utilities/helpers';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { LoadingIndicator } from '../../../shared/loading-indicator/loading-indicator';

@Component({
  selector: 'app-school-profile',
  imports: [ShimmerEffect, ReactiveFormsModule, FormsModule, LoadingIndicator],
  templateUrl: './school-profile.html',
  styleUrl: './school-profile.css',
})
export class SchoolProfile {
  constructor(
    private session: Session,
    private schoolService: SchoolService,
  ) {
    const token = this.session.getItem('token');

    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.schoolId) {
      this.getSchoolById(this.schoolId);
    }

    effect(() => {
      const school = this.school()?.payload;

      if (!school) {
        return;
      }

      this.location = school.location;
      this.phone = school.phone;
      this.primaryColor = school.primary_color;
      this.secondaryColor = school.secondary_color;
      this.imagePreview = school.school_img;
    });
  }

  protected isLoading = signal<boolean>(false);
  protected school = signal<ISchool | null>(null);
  protected readonly flashService = inject(FlashService);
  protected schoolId?: string;
  protected readonly Helpers = Helpers;
  protected primaryColor = '#003C54';
  protected secondaryColor!: string;
  protected phone!: string;
  protected schoolLogoImage?: File;
  protected imagePreview?: string;
  protected isSubmitting = signal(false);
  protected location!: string;

  protected getSchoolById(id: string): void {
    this.isLoading.set(true);

    this.schoolService.getSchoolById(`${ApiEndpoints.url}/school/${id}`).subscribe({
      next: (data) => {
        this.school.set(data);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Unable to load school data. Refresh the page');
      },
    });
  }

  protected onFileSelected(event: Event, form: NgForm): void {
    const input = event.target as HTMLInputElement;

    const file = input.files?.[0];

    if (!file) {
      return;
    }

    form.control.markAsDirty();
    form.control.markAsTouched();

    if (this.imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreview);
    }

    this.schoolLogoImage = file;
    this.imagePreview = URL.createObjectURL(file);
  }

  protected updateSchoolProfile(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');

      form.control.markAllAsTouched();
      return;
    }

    if (this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    const schoolForm = new FormData();

    schoolForm.append('primary_color', this.primaryColor);

    schoolForm.append('secondary_color', this.secondaryColor);

    schoolForm.append('phone', this.phone);

    if (this.schoolLogoImage) {
      schoolForm.append('school_img', this.schoolLogoImage);
    }

    this.schoolService
      .updateSchoolBrand(`${ApiEndpoints.url}/school/${this.schoolId}/edit`, schoolForm)
      .subscribe({
        next: () => {
          this.flashService.success('Successfully updated school profile.');
          window.location.reload();
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Failed to update school profile. Try again.');
        },
      });
  }
}
